import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import {
  buildSubscriptionSnapshot,
  canAccessFeature,
  getFeatureAccessState,
  getFeaturePolicy,
} from '../services/entitlementService';
import { getStoredSubscriptionClaims } from '../services/jwtClaims';
import {
  AUTH_LOGGED_OUT_EVENT,
  AUTH_TOKENS_UPDATED_EVENT,
} from '../services/tokenStorage';
import { useAdminOverride, OVERRIDE_MODES } from './AdminOverrideContext';
import {
  ENTITLEMENT_KEYS,
  SUBSCRIPTION_STATUS,
  SUBSCRIPTION_TIER,
} from '../config/entitlements';

// ─── Override snapshot ────────────────────────────────────────────────────────
// Builds a synthetic "all-access" subscription snapshot used when a global
// admin override is active.  All known entitlement keys are granted so every
// feature gate opens automatically without touching any user records.
function buildOverrideSnapshot() {
  const allEntitlements = Object.values(ENTITLEMENT_KEYS);
  return {
    status: SUBSCRIPTION_STATUS.ACTIVE,
    tier: SUBSCRIPTION_TIER.PRO,
    expiryDate: null,
    isExpired: false,
    isActive: true,
    entitlements: allEntitlements,
    entitlementSet: new Set(allEntitlements),
  };
}

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children }) {
  const { user, loading, fetchSubscriptionStatus } = useAuth();
  const [tokenClaims, setTokenClaims] = useState(() => getStoredSubscriptionClaims());
  const { overrideEnabled, overrideMode } = useAdminOverride();

  useEffect(() => {
    const syncClaims = () => setTokenClaims(getStoredSubscriptionClaims());

    window.addEventListener(AUTH_TOKENS_UPDATED_EVENT, syncClaims);
    window.addEventListener(AUTH_LOGGED_OUT_EVENT, syncClaims);

    return () => {
      window.removeEventListener(AUTH_TOKENS_UPDATED_EVENT, syncClaims);
      window.removeEventListener(AUTH_LOGGED_OUT_EVENT, syncClaims);
    };
  }, []);

  // Real subscription snapshot derived from user data and JWT claims.
  const realSubscription = useMemo(
    () => buildSubscriptionSnapshot(user, tokenClaims),
    [user, tokenClaims],
  );

  // When a global admin override is active (and the mode is not 'normal'),
  // replace the real snapshot with a synthetic all-access Pro snapshot.
  // This is the single intercept point — every downstream hook and component
  // that reads subscription data is automatically affected without any
  // individual code changes.
  const subscription = useMemo(() => {
    if (overrideEnabled && overrideMode !== OVERRIDE_MODES.NORMAL) {
      return buildOverrideSnapshot();
    }
    return realSubscription;
  }, [overrideEnabled, overrideMode, realSubscription]);

  const value = useMemo(() => {
    const canAccess = featureKey => canAccessFeature(subscription, featureKey);
    const getAccessState = featureKey => getFeatureAccessState(subscription, featureKey);

    return {
      loading,
      subscription,
      refreshSubscription: fetchSubscriptionStatus,
      tokenClaims,
      canAccess,
      getFeaturePolicy,
      getAccessState,
    };
  }, [fetchSubscriptionStatus, loading, subscription, tokenClaims]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);

  if (context === null) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }

  return context;
}

export function useEntitlements() {
  const { subscription, canAccess, getFeaturePolicy, getAccessState } = useSubscription();

  return useMemo(() => ({
    subscription,
    canAccess,
    getFeaturePolicy,
    getAccessState,
  }), [subscription, canAccess, getFeaturePolicy, getAccessState]);
}

export function useFeatureAccess(featureKey) {
  const { getAccessState } = useSubscription();

  return useMemo(() => getAccessState(featureKey), [featureKey, getAccessState]);
}